import {
  SectionV3WithSteps,
  ResolvedStep,
  DateFrame,
  ParticipantSection,
} from '../types/v3Types';
import type { Challenge } from '@wix/ambassador-challenges-v1-challenge/types';
import { IParticipantSectionsContext } from '../contexts/ParticipantSections/ParticipantSectionsContext';
import { toParticipantStepV3 } from '../contexts/ParticipantStepsDataProvider/toParticipantSteps';
import { getStartDate } from './GetStartDate';
import { isFlexible } from './isFlexible';
import { isSelfPaced } from './isSelfPaced';
import { addDays } from 'date-fns';
import isAfter from 'date-fns/isAfter';
import groupBy from 'lodash/groupBy';
import { getRightDateFromBackend } from './dates';

export function toParticipantSectionsV3({
  sections,
  resolvedSteps,
  challenge,
  dateFrame,
}: {
  sections: SectionV3WithSteps[];
  challenge: Challenge;
  dateFrame?: DateFrame;
  resolvedSteps: ResolvedStep[];
}): {
  sections: IParticipantSectionsContext['listParticipantSections'];
  steps: IParticipantSectionsContext['participantSteps']['steps'];
} {
  const resolvedById = new Map(
    resolvedSteps.map((step) => [step.programStepId, step]),
  );
  const initialDate = isFlexible(challenge)
    ? getRightDateFromBackend(dateFrame.start)
    : getStartDate(challenge);
  const participantSections = sections
    .sort((a, b) => {
      if (isSelfPaced(challenge)) {
        return a.ordering - b.ordering;
      } else if (a.delayInDays !== b.delayInDays) {
        return a.delayInDays - b.delayInDays;
      }
      return a.ordering - b.ordering;
    })
    .map((section) => {
      const steps = section.steps.map((step) => {
        const resolvedStep = resolvedById.get(step.id);
        return toParticipantStepV3({
          step,
          resolvedStep,
          dateFrame: !initialDate ? dateFrame : undefined,
          initialDate,
        });
      });
      const result: ParticipantSection = {
        id: section.id,
        source: section,
        steps,
        waitingDate: undefined,
      };

      if (isSelfPaced(challenge) && section.delayInDays) {
        const startDate = addDays(
          new Date(dateFrame.start),
          section.delayInDays,
        );
        if (isAfter(startDate, new Date())) {
          result.waitingDate = startDate as unknown as string;
        }
      }

      return result;
    });
  const participantSteps = participantSections.flatMap(
    (section) => section.steps,
  );

  if (!isSelfPaced(challenge)) {
    const filteredSections = participantSections.filter((section) => {
      // filter out sections with no steps for scheduled programs;
      return section?.steps?.length;
    });
    // group filteredSections by delayInDays
    const groupedSections = groupBy(
      filteredSections,
      (section) => section.source.delayInDays,
    );

    // Skip merging logic if no grouping occurred
    if (Object.keys(groupedSections).length === filteredSections.length) {
      return {
        sections: filteredSections,
        steps: participantSteps,
      };
    }

    const mergedSections = Object.values(groupedSections).map(
      (sectionGroup) => {
        if (sectionGroup.length === 1) {
          return sectionGroup[0];
        } else {
          // Multiple sections with same delayInDays - merge them
          const firstSection = sectionGroup[0];
          const allSteps = sectionGroup.flatMap((section) => section.steps);

          return {
            ...firstSection,
            steps: allSteps,
          };
        }
      },
    );

    return {
      sections: mergedSections,
      steps: participantSteps,
    };
  }
  return {
    sections: participantSections,
    steps: participantSteps,
  };
}
